// client/src/pages/Contacts.js
import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth.js';
import api from '../services/api.js';
import contactService from '../services/contactService.js';
import Button from '../components/ui/Button.js';

const MONTHS = [
  { value: '', label: 'Month' },
  { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
  { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
  { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
  { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' },
];

const MONTH_ABBR = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const TIMEZONE_REGION_ORDER = ['US', 'Canada', 'Europe', 'Americas', 'Middle East', 'Asia', 'Africa', 'Pacific', 'Other'];

function daysInMonth(month) {
  if (!month) return 31;
  return [0, 31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month] || 31;
}

function formatBirthday(m, d) {
  if (!m || !d) return '';
  return `${MONTH_ABBR[m]} ${d}`;
}

function formatTimezoneShort(tzcode) {
  if (!tzcode) return '';
  return tzcode.split('/').pop().replace(/_/g, ' ');
}

// ─── Contact Limit Banner ─────────────────────────────────
function ContactLimitBanner({ used, limit, remaining, isUnlimited }) {
  if (isUnlimited) return null;

  const percentUsed = limit > 0 ? Math.min(100, (used / limit) * 100) : 0;
  const isNearLimit = remaining <= 5;
  const isAtLimit = remaining === 0;

  return (
    <div className={`p-3 rounded-lg border mb-5 ${
      isAtLimit ? 'bg-red-900/20 border-red-600/40' : 
      isNearLimit ? 'bg-amber-900/20 border-amber-600/40' : 
      'bg-royal-900 dark:bg-royal-800/40 border-royal-700 dark:border-royal-600'
    }`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-royal-100 text-sm">Contact Limit</span>
        <span className={`text-sm font-medium ${
          isAtLimit ? 'text-red-400' : isNearLimit ? 'text-amber-400' : 'text-white'
        }`}>
          {used} / {limit}
        </span>
      </div>
      <div className="w-full bg-royal-700 rounded-full h-2">
        <div
          className={`h-2 rounded-full transition-all ${
            isAtLimit ? 'bg-red-500' : isNearLimit ? 'bg-amber-500' : 'bg-gold'
          }`}
          style={{ width: `${percentUsed}%` }}
        />
      </div>
      {isAtLimit ? (
        <div className="flex items-center justify-between mt-2">
          <p className="text-red-400 text-sm">You've reached your contact limit.</p>
          <a href="/pricing" className="text-sm text-gold hover:text-gold-light underline">Upgrade →</a>
        </div>
      ) : isNearLimit ? (
        <p className="text-amber-400 text-sm mt-1.5">
          {remaining} contact{remaining !== 1 ? 's' : ''} remaining. <a href="/pricing" className="underline hover:text-gold">Upgrade</a> for more.
        </p>
      ) : null}
    </div>
  );
}

// ─── Google Import Preview Modal ──────────────────────────
function GoogleImportModal({ contacts, onClose, onImport }) {
  const [selected, setSelected] = useState(() => new Set(contacts.map((_, i) => i)));
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [searchFilter, setSearchFilter] = useState('');

  const filteredContacts = contacts.filter((c) => {
    if (!searchFilter) return true;
    const term = searchFilter.toLowerCase();
    return (
      (c.email && c.email.toLowerCase().includes(term)) ||
      (c.firstname && c.firstname.toLowerCase().includes(term)) ||
      (c.lastname && c.lastname.toLowerCase().includes(term))
    );
  });

  const toggleContact = (index) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === contacts.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(contacts.map((_, i) => i)));
    }
  };

  const handleImport = async () => {
    const selectedContacts = contacts.filter((_, i) => selected.has(i));
    if (selectedContacts.length === 0) {
      setError('Please select at least one contact to import');
      return;
    }

    setImporting(true);
    setError('');

    try {
      const data = await contactService.bulkImportContacts(selectedContacts);
      setResults(data.results);
      if (data.results.imported > 0) {
        onImport();
      }
    } catch (err) {
      setError(err.message || 'Failed to import contacts');
    }

    setImporting(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
      <div className="bg-royal-900 border border-royal-700 rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 sm:p-6 pb-4 border-b border-royal-700/50">
          <div>
            <h2 className="text-gold font-display text-lg sm:text-xl">Import Google Contacts</h2>
            <p className="text-royal-100 text-sm mt-1">
              {contacts.length} contact{contacts.length !== 1 ? 's' : ''} found · {selected.size} selected
            </p>
          </div>
          <button onClick={onClose} className="text-royal-100 hover:text-white transition-colors text-xl p-1">✕</button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6 pt-4 space-y-4">
          {error && (
            <div className="p-3 bg-red-900/20 border border-red-600/40 rounded-lg">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {results && (
            <div className={`p-4 rounded-lg border ${results.imported > 0 ? 'bg-green-900/20 border-green-600/40' : 'bg-amber-900/20 border-amber-600/40'}`}>
              <h3 className={`font-medium text-base mb-2 ${results.imported > 0 ? 'text-green-300' : 'text-amber-300'}`}>
                {results.imported > 0 ? '✓ Import Complete' : 'Import Results'}
              </h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="text-royal-100">Selected:</div>
                <div className="text-white">{results.total}</div>
                <div className="text-royal-100">Imported:</div>
                <div className="text-green-400">{results.imported}</div>
                <div className="text-royal-100">Skipped:</div>
                <div className="text-amber-400">{results.skipped}</div>
              </div>

              {results.duplicates?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-royal-700/50">
                  <p className="text-royal-100 text-sm mb-1">Already in your contacts:</p>
                  <p className="text-royal-100 text-sm">{results.duplicates.join(', ')}</p>
                </div>
              )}
            </div>
          )}

          {!results && (
            <>
              {/* Search and Select All */}
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1 relative">
                  <input
                    type="text"
                    placeholder="Filter contacts..."
                    value={searchFilter}
                    onChange={(e) => setSearchFilter(e.target.value)}
                    className="w-full pl-10 pr-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white placeholder-royal-200 focus:outline-none focus:border-gold/50 transition-colors"
                  />
                  <svg className="absolute left-3 top-3 w-5 h-5 text-royal-100" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <button
                  onClick={toggleAll}
                  className="px-4 py-2.5 text-sm text-royal-100 border border-royal-600 rounded-lg hover:text-white hover:border-royal-200 transition-colors whitespace-nowrap"
                >
                  {selected.size === contacts.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>

              {/* Contact List */}
              <div className="space-y-1 max-h-[400px] overflow-y-auto">
                {filteredContacts.length === 0 ? (
                  <p className="text-royal-100 text-sm text-center py-6">No contacts match your filter</p>
                ) : (
                  filteredContacts.map((contact, filteredIndex) => {
                    const originalIndex = contacts.indexOf(contact);
                    const isSelected = selected.has(originalIndex);
                    const name = [contact.firstname, contact.lastname].filter(Boolean).join(' ');

                    return (
                      <label
                        key={originalIndex}
                        className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                          isSelected ? 'bg-gold/5 border border-gold/20' : 'bg-royal-800/20 border border-transparent hover:bg-royal-800/40'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleContact(originalIndex)}
                          className="w-4 h-4 rounded border-royal-600 text-gold focus:ring-gold/50 bg-royal-800"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm font-medium truncate">
                            {name || contact.email}
                          </p>
                          <p className="text-royal-100 text-sm truncate">
                            {name ? contact.email : ''}
                            {contact.birthmonth && contact.birthdate && (
                              <span className="ml-2">🎂 {formatBirthday(contact.birthmonth, contact.birthdate)}</span>
                            )}
                          </p>
                        </div>
                      </label>
                    );
                  })
                )}
              </div>
            </>
          )}
        </div>

        <div className="flex justify-end gap-3 p-4 sm:p-6 pt-4 border-t border-royal-700/50">
          <button onClick={onClose} className="px-4 py-2.5 text-royal-100 text-base hover:text-white transition-colors">
            {results ? 'Close' : 'Cancel'}
          </button>
          {!results && (
            <Button onClick={handleImport} disabled={selected.size === 0 || importing}>
              {importing ? 'Importing...' : `Import ${selected.size} Contact${selected.size !== 1 ? 's' : ''}`}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── CSV Import Modal ─────────────────────────────────────
function CSVImportModal({ onClose, onSuccess }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.csv')) {
        setError('Please select a CSV file');
        setFile(null);
        return;
      }
      setFile(selectedFile);
      setError('');
      setResults(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      if (!droppedFile.name.endsWith('.csv')) {
        setError('Please select a CSV file');
        return;
      }
      setFile(droppedFile);
      setError('');
      setResults(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a CSV file');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const data = await contactService.importCSV(file);
      setResults(data.results);
      if (data.results.imported > 0) {
        onSuccess();
      }
    } catch (err) {
      setError(err.message || 'Failed to import CSV');
    }

    setUploading(false);
  };

  const handleDownloadTemplate = () => {
    contactService.downloadTemplate();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
      <div className="bg-royal-900 border border-royal-700 rounded-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 sm:p-6 pb-4 border-b border-royal-700/50">
          <h2 className="text-gold font-display text-lg sm:text-xl">Import Contacts from CSV</h2>
          <button onClick={onClose} className="text-royal-100 hover:text-white transition-colors text-xl p-1">✕</button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6 pt-4 space-y-4">
          <div className="bg-royal-800/40 border border-royal-700/50 rounded-lg p-3 sm:p-4">
            <h3 className="text-white font-medium text-base mb-2">How to prepare your CSV</h3>
            <ol className="text-royal-100 text-sm space-y-1.5 list-decimal list-inside">
              <li>Create a spreadsheet in Google Sheets, Excel, or Numbers</li>
              <li>Add columns: <span className="text-gold">email</span> (required), firstname, lastname, birthmonth, birthdate, phone, timezone, groups</li>
              <li>Download as CSV (File → Download → CSV)</li>
              <li>Upload your file below</li>
            </ol>
            <button
              onClick={handleDownloadTemplate}
              className="mt-3 text-sm text-gold hover:text-gold-light underline"
            >
              Download sample template →
            </button>
          </div>

          {error && (
            <div className="p-3 bg-red-900/20 border border-red-600/40 rounded-lg">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {results && (
            <div className={`p-4 rounded-lg border ${results.imported > 0 ? 'bg-green-900/20 border-green-600/40' : 'bg-amber-900/20 border-amber-600/40'}`}>
              <h3 className={`font-medium text-base mb-2 ${results.imported > 0 ? 'text-green-300' : 'text-amber-300'}`}>
                {results.imported > 0 ? '✓ Import Complete' : 'Import Results'}
              </h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="text-royal-100">Total rows:</div>
                <div className="text-white">{results.total}</div>
                <div className="text-royal-100">Imported:</div>
                <div className="text-green-400">{results.imported}</div>
                <div className="text-royal-100">Skipped:</div>
                <div className="text-amber-400">{results.skipped}</div>
              </div>

              {results.duplicates?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-royal-700/50">
                  <p className="text-royal-100 text-sm mb-1">Duplicates (already exist):</p>
                  <p className="text-royal-100 text-sm">{results.duplicates.join(', ')}</p>
                </div>
              )}

              {results.invalidEmails?.length > 0 && (
                <div className="mt-2">
                  <p className="text-royal-100 text-sm mb-1">Invalid emails:</p>
                  <p className="text-red-400 text-sm">{results.invalidEmails.join(', ')}</p>
                </div>
              )}
            </div>
          )}

          {!results && (
            <div
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-xl p-6 sm:p-8 text-center cursor-pointer transition-colors ${
                file ? 'border-gold bg-gold/5' : 'border-royal-600 hover:border-royal-400'
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="hidden"
              />
              
              {file ? (
                <div>
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gold/10 flex items-center justify-center">
                    <svg className="w-6 h-6 text-gold" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <p className="text-gold font-medium text-base">{file.name}</p>
                  <p className="text-royal-100 text-sm mt-1">{(file.size / 1024).toFixed(1)} KB</p>
                  <p className="text-royal-100 text-sm mt-2">Click to change file</p>
                </div>
              ) : (
                <div>
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-royal-800 flex items-center justify-center">
                    <svg className="w-6 h-6 text-royal-100" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                  </div>
                  <p className="text-white font-medium text-base">Drop your CSV file here</p>
                  <p className="text-royal-100 text-sm mt-1">or click to browse</p>
                </div>
              )}
            </div>
          )}

          {!results && (
            <div className="text-royal-100 text-sm space-y-1">
              <p>• Duplicate emails will be skipped automatically</p>
              <p>• Groups column: separate multiple groups with semicolons (e.g., "Family;Friends")</p>
              <p>• Maximum file size: 5MB</p>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 p-4 sm:p-6 pt-4 border-t border-royal-700/50">
          <button onClick={onClose} className="px-4 py-2.5 text-royal-100 text-base hover:text-white transition-colors">
            {results ? 'Close' : 'Cancel'}
          </button>
          {!results && (
            <Button onClick={handleUpload} disabled={!file || uploading}>
              {uploading ? 'Importing...' : 'Import Contacts'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Birthday Picker ──────────────────────────────────────
function BirthdayPicker({ birthmonth, birthdate, onChange }) {
  const maxDay = daysInMonth(birthmonth ? parseInt(birthmonth) : null);

  return (
    <div className="grid grid-cols-2 gap-2">
      <select
        value={birthmonth || ''}
        onChange={(e) => {
          const newMonth = e.target.value ? parseInt(e.target.value) : '';
          onChange({ birthmonth: newMonth || null, birthdate: (birthdate && birthdate > daysInMonth(newMonth)) ? null : birthdate });
        }}
        className="px-3 py-2.5 bg-royal-900/60 border border-royal-600 rounded-lg text-base text-white focus:outline-none focus:border-gold/50 transition-colors"
      >
        {MONTHS.map((m) => (
          <option key={m.value} value={m.value}>{m.label}</option>
        ))}
      </select>
      <select
        value={birthdate || ''}
        onChange={(e) => onChange({ birthmonth, birthdate: e.target.value ? parseInt(e.target.value) : null })}
        className="px-3 py-2.5 bg-royal-900/60 border border-royal-600 rounded-lg text-base text-white focus:outline-none focus:border-gold/50 transition-colors"
      >
        <option value="">Day</option>
        {Array.from({ length: maxDay }, (_, i) => i + 1).map((d) => (
          <option key={d} value={d}>{d}</option>
        ))}
      </select>
    </div>
  );
}

// ─── Household Member Modal ───────────────────────────────
function HouseholdMemberModal({ member, contactName, onSave, onClose, saving }) {
  const [firstname, setFirstname] = useState(member?.firstname || '');
  const [birthmonth, setBirthmonth] = useState(member?.birthmonth || '');
  const [birthdate, setBirthdate] = useState(member?.birthdate || '');
  const [notes, setNotes] = useState(member?.notes || '');
  const [error, setError] = useState('');

  const isEditing = !!member?.memberid;

  const handleSave = () => {
    setError('');
    if (!firstname.trim()) { setError('First name is required'); return; }
    if (!birthmonth) { setError('Birth month is required'); return; }
    if (!birthdate) { setError('Birth day is required'); return; }
    onSave({ firstname: firstname.trim(), birthmonth: parseInt(birthmonth), birthdate: parseInt(birthdate), notes: notes || null });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
      <div className="bg-royal-900 border border-royal-700 rounded-2xl w-full max-w-md p-4 sm:p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="text-gold font-display text-lg sm:text-xl">{isEditing ? 'Edit Household Member' : 'Add Household Member'}</h3>
            <p className="text-royal-100 text-sm mt-1">For {contactName}</p>
          </div>
          <button onClick={onClose} className="text-royal-100 hover:text-white transition-colors text-2xl leading-none">×</button>
        </div>

        {error && <p className="text-red-400 text-base mb-4">{error}</p>}

        <div className="space-y-4">
          <div>
            <label className="block text-sm text-royal-100 mb-1.5">First Name *</label>
            <input type="text" value={firstname} onChange={(e) => setFirstname(e.target.value)} placeholder="First name"
              className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white placeholder-royal-200 focus:outline-none focus:border-gold/50 transition-colors" autoFocus />
          </div>

          <div>
            <label className="block text-sm text-royal-100 mb-1.5">
              Birthday *
              <span className="text-royal-200/60 font-normal ml-1">— for birthday auto send</span>
            </label>
            <div className="grid grid-cols-2 gap-2">
              <select value={birthmonth} onChange={(e) => { const m = e.target.value ? parseInt(e.target.value) : ''; setBirthmonth(m); if (birthdate && birthdate > daysInMonth(m)) setBirthdate(''); }}
                className="px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white focus:outline-none focus:border-gold/50 transition-colors">
                {MONTHS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
              <select value={birthdate} onChange={(e) => setBirthdate(e.target.value ? parseInt(e.target.value) : '')}
                className="px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white focus:outline-none focus:border-gold/50 transition-colors">
                <option value="">Day</option>
                {Array.from({ length: daysInMonth(birthmonth ? parseInt(birthmonth) : null) }, (_, i) => i + 1).map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm text-royal-100 mb-1.5">Notes</label>
            <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional notes"
              className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white placeholder-royal-200 focus:outline-none focus:border-gold/50 transition-colors" />
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <Button onClick={handleSave} disabled={saving} className="flex-1">
            {saving ? 'Saving...' : isEditing ? 'Save Changes' : 'Add Member'}
          </Button>
          <button onClick={onClose} className="px-4 py-2.5 text-royal-100 border border-royal-600 rounded-lg text-base hover:text-white hover:border-royal-200 transition-colors">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Household Member Row ─────────────────────────────────
function HouseholdMemberRow({ member, onEdit, onDelete, deleting }) {
  return (
    <div className="flex items-center gap-2 sm:gap-3 p-3 bg-royal-800/40 border border-royal-600 rounded-lg group hover:border-royal-200 transition-colors">
      <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-gold/10 flex items-center justify-center flex-shrink-0">
        <span className="text-gold text-sm font-bold">{member.firstname[0].toUpperCase()}</span>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-white text-base font-medium truncate">{member.firstname}</p>
        <p className="text-royal-100 text-sm truncate">
          🎂 {formatBirthday(member.birthmonth, member.birthdate)}
          {member.notes && <span className="ml-2">· {member.notes}</span>}
        </p>
      </div>
      <div className="flex gap-1 flex-shrink-0">
        <button onClick={() => onEdit(member)} className="p-2 text-royal-100 hover:text-gold rounded-lg hover:bg-royal-800/60 transition-colors" title="Edit">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        </button>
        <button onClick={() => onDelete(member)} disabled={deleting} className="p-2 text-royal-100 hover:text-red-400 rounded-lg hover:bg-royal-800/60 transition-colors disabled:opacity-60" title="Delete">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      </div>
    </div>
  );
}

// ─── Contact Accordion ────────────────────────────────────
function ContactAccordion({ contact, onEdit, onDelete, onRefresh }) {
  const [expanded, setExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [editingMember, setEditingMember] = useState(null);
  const [savingMember, setSavingMember] = useState(false);
  const [deletingMember, setDeletingMember] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const name = [contact.firstname, contact.lastname].filter(Boolean).join(' ');
  const displayName = name || contact.email;
  const householdMembers = contact.household_members || [];
  const hasHousehold = householdMembers.length > 0;
  const contactTypes = contact.types || [];

  const handleDeleteContact = async () => {
    const memberWarning = hasHousehold ? ` and ${householdMembers.length} household member(s)` : '';
    if (!window.confirm(`Delete ${displayName}${memberWarning}? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await api.delete(`/contacts/${contact.contactid}`);
      onDelete();
    } catch (err) { console.error('Delete contact error:', err); }
    setDeleting(false);
  };

  const handleSaveMember = async (data) => {
    setSavingMember(true);
    try {
      if (editingMember.memberid) {
        await api.put(`/contacts/${contact.contactid}/household/${editingMember.memberid}`, data);
      } else {
        await api.post(`/contacts/${contact.contactid}/household`, data);
      }
      onRefresh();
      setEditingMember(null);
    } catch (err) { console.error('Save household member error:', err); }
    setSavingMember(false);
  };

  const handleDeleteMember = async (member) => {
    if (!window.confirm(`Remove ${member.firstname} from this household?`)) return;
    setDeletingMember(true);
    try {
      await api.delete(`/contacts/${contact.contactid}/household/${member.memberid}`);
      onRefresh();
    } catch (err) { console.error('Delete household member error:', err); }
    setDeletingMember(false);
  };

  return (
    <div className="border border-royal-700 dark:border-royal-700/40 rounded-xl overflow-hidden bg-royal-900 dark:bg-royal-800/20">
      <div className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-3 cursor-pointer hover:bg-royal-800/30 transition-colors" onClick={() => setExpanded(!expanded)}>
        <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-gold/10 flex items-center justify-center flex-shrink-0">
          <span className="text-gold text-sm sm:text-base font-bold">{displayName[0].toUpperCase()}</span>
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-white text-sm sm:text-base font-medium truncate">
            {displayName}
            {hasHousehold && <span className="text-royal-100 text-sm ml-1 sm:ml-2 font-normal">+{householdMembers.length}</span>}
          </p>
          <p className="text-royal-100 text-sm truncate hidden sm:block">{contact.email}</p>
        </div>

        <button onClick={(e) => { e.stopPropagation(); onEdit(contact); }} className="p-2 text-royal-100 hover:text-gold rounded-lg hover:bg-royal-800/60 transition-colors flex-shrink-0" title="Edit contact">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        </button>

        <div className="text-royal-100 flex-shrink-0">
          <svg className={`w-5 h-5 transition-transform ${expanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-royal-700/40">
          <div className="flex gap-2 p-3 pb-0">
            <button onClick={() => setActiveTab('general')} className={`px-3 sm:px-4 py-1.5 text-sm font-medium rounded-lg transition-colors ${activeTab === 'general' ? 'bg-gold text-royal-900' : 'bg-royal-800/60 text-royal-100 hover:text-white'}`}>
              General
            </button>
            <button onClick={() => setActiveTab('household')} className={`px-3 sm:px-4 py-1.5 text-sm font-medium rounded-lg transition-colors ${activeTab === 'household' ? 'bg-gold text-royal-900' : 'bg-royal-800/60 text-royal-100 hover:text-white'}`}>
              Household{hasHousehold && ` (${householdMembers.length})`}
            </button>
          </div>

          <div className="p-3 sm:p-4">
            {activeTab === 'general' && (
              <div className="space-y-4">
                <div className="sm:hidden">
                  <p className="text-royal-100 text-sm mb-1">Email</p>
                  <p className="text-white text-base break-all">{contact.email}</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {(contact.birthmonth && contact.birthdate) && (
                    <div>
                      <p className="text-royal-100 text-sm mb-1">Birthday</p>
                      <p className="text-white text-base">🎂 {formatBirthday(contact.birthmonth, contact.birthdate)}</p>
                    </div>
                  )}
                  {contact.timezone && (
                    <div>
                      <p className="text-royal-100 text-sm mb-1">Timezone</p>
                      <p className="text-white text-base">🕐 {formatTimezoneShort(contact.timezone)}</p>
                    </div>
                  )}
                </div>

                {contact.notes && (
                  <div>
                    <p className="text-royal-100 text-sm mb-1">Notes</p>
                    <p className="text-white text-base whitespace-pre-line">{contact.notes}</p>
                  </div>
                )}

                {contactTypes.length > 0 && (
                  <div>
                    <p className="text-royal-100 text-sm mb-2">Groups</p>
                    <div className="flex flex-wrap gap-2">
                      {contactTypes.map((t) => (
                        <span key={t.contacttypeid} className="text-sm bg-royal-800 text-royal-100 px-3 py-1 rounded-full">{t.typename}</span>
                      ))}
                    </div>
                  </div>
                )}

                {!contact.birthmonth && !contact.timezone && !contact.notes && contactTypes.length === 0 && (
                  <p className="text-royal-100 text-sm text-center py-4">No additional information</p>
                )}

                <div className="pt-2 border-t border-royal-700/50">
                  <button onClick={handleDeleteContact} disabled={deleting} className="text-red-400 text-sm hover:text-red-300 transition-colors disabled:opacity-60">
                    {deleting ? 'Deleting...' : 'Delete Contact'}
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'household' && (
              <div className="space-y-3">
                {householdMembers.length === 0 ? (
                  <p className="text-royal-100 text-sm text-center py-4">No household members yet</p>
                ) : (
                  householdMembers.map((hm) => (
                    <HouseholdMemberRow key={hm.memberid} member={hm} onEdit={setEditingMember} onDelete={handleDeleteMember} deleting={deletingMember} />
                  ))
                )}

                <button onClick={() => setEditingMember({})} className="w-full p-3 border-2 border-dashed border-royal-600 rounded-lg text-royal-100 text-base font-medium hover:text-gold hover:border-gold/50 transition-colors">
                  + Add Household Member
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {editingMember !== null && (
        <HouseholdMemberModal member={editingMember} contactName={displayName} onSave={handleSaveMember} onClose={() => setEditingMember(null)} saving={savingMember} />
      )}
    </div>
  );
}

// ─── Contact Edit Modal ───────────────────────────────────
function ContactEditModal({ contact, types, timezoneGrouped, onSave, onClose }) {
  const [form, setForm] = useState({
    email: contact?.email || '',
    firstname: contact?.firstname || '',
    lastname: contact?.lastname || '',
    phone: contact?.phone || '',
    birthmonth: contact?.birthmonth || '',
    birthdate: contact?.birthdate || '',
    timezone: contact?.timezone || '',
    notes: contact?.notes || '',
    typeIds: (contact?.types || []).map((t) => t.contacttypeid),
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [duplicatePrompt, setDuplicatePrompt] = useState(null);
  const [showHouseholdAdd, setShowHouseholdAdd] = useState(false);
  const [hmFirstname, setHmFirstname] = useState('');
  const [hmBirthmonth, setHmBirthmonth] = useState('');
  const [hmBirthdate, setHmBirthdate] = useState('');
  const [hmNotes, setHmNotes] = useState('');
  const [hmSaving, setHmSaving] = useState(false);

  const isNew = !contact;

  const handleSave = async () => {
    setError('');
    if (!form.email.trim()) { setError('Email is required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, birthmonth: form.birthmonth || null, birthdate: form.birthdate || null, timezone: form.timezone || null };
      if (contact) await api.put(`/contacts/${contact.contactid}`, payload);
      else await api.post('/contacts', payload);
      onSave();
    } catch (err) {
      const errMsg = err.data?.error || 'Failed to save contact';
      if (isNew && (err.status === 400 || err.status === 409) && errMsg.toLowerCase().includes('limit') === false) {
        try {
          const searchResp = await api.get(`/contacts?search=${encodeURIComponent(form.email.toLowerCase().trim())}&limit=1`);
          const existing = (searchResp.contacts || []).find(c => c.email.toLowerCase() === form.email.toLowerCase().trim());
          if (existing) {
            const existingName = [existing.firstname, existing.lastname].filter(Boolean).join(' ') || existing.email;
            setDuplicatePrompt({ contactid: existing.contactid, name: existingName, email: existing.email });
            setSaving(false);
            return;
          }
        } catch { /* fall through to generic error */ }
      }
      setError(errMsg);
    }
    setSaving(false);
  };

  const handleAddHouseholdMember = async () => {
    if (!hmFirstname.trim()) { setError("First name is required"); return; }
    if (!hmBirthmonth) { setError("Birth month is required"); return; }
    if (!hmBirthdate) { setError("Birth day is required"); return; }
    setHmSaving(true);
    setError('');
    try {
      await api.post(`/contacts/${duplicatePrompt.contactid}/household`, {
        firstname: hmFirstname.trim(),
        birthmonth: parseInt(hmBirthmonth),
        birthdate: parseInt(hmBirthdate),
        notes: hmNotes || null,
      });
      onSave();
    } catch (err) {
      setError(err.data?.error || 'Failed to add household member');
    }
    setHmSaving(false);
  };

  const toggleType = (typeId) => {
    setForm((prev) => ({
      ...prev,
      typeIds: prev.typeIds.includes(typeId) ? prev.typeIds.filter((id) => id !== typeId) : [...prev.typeIds, typeId],
    }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-3 sm:px-4">
      <div className="bg-royal-900 border border-royal-700 rounded-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 sm:p-6 pb-4 border-b border-royal-700/50">
          <h2 className="text-gold font-display text-lg sm:text-xl">{contact ? 'Edit Contact' : 'New Contact'}</h2>
          <button onClick={onClose} className="text-royal-100 hover:text-white transition-colors text-xl p-1">✕</button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6 pt-4 space-y-4">
          {error && <p className="text-red-400 text-base">{error}</p>}

          {duplicatePrompt && !showHouseholdAdd && (
            <div className="p-4 bg-royal-800/40 border border-royal-600 rounded-xl space-y-3">
              <p className="text-white text-sm">
                A contact with the email <span className="text-gold font-medium">{duplicatePrompt.email}</span> already
                exists (<span className="text-white font-medium">{duplicatePrompt.name}</span>).
              </p>
              <p className="text-white/70 text-sm">Would you like to add a household member to this contact instead?</p>
              <div className="flex gap-3">
                <button onClick={() => { setDuplicatePrompt(null); setError('A contact with this email already exists.'); }}
                  className="flex-1 px-3 py-2 border border-royal-600 text-white rounded-lg text-sm hover:border-royal-200 transition-colors">
                  No, go back
                </button>
                <button onClick={() => setShowHouseholdAdd(true)}
                  className="flex-1 px-3 py-2 bg-gold text-royal-900 font-semibold rounded-lg text-sm hover:bg-gold-light transition-colors">
                  Yes, add household member
                </button>
              </div>
            </div>
          )}

          {showHouseholdAdd && duplicatePrompt && (
            <div className="p-4 bg-royal-800/40 border border-gold/30 rounded-xl space-y-3">
              <p className="text-gold text-sm font-medium">Add household member to {duplicatePrompt.name}</p>
              <div>
                <label className="block text-sm text-royal-100 mb-1.5">First Name *</label>
                <input type="text" value={hmFirstname} onChange={(e) => setHmFirstname(e.target.value)} placeholder="First name"
                  className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white placeholder-royal-200 focus:outline-none focus:border-gold/50 transition-colors" autoFocus />
              </div>
              <div>
                <label className="block text-sm text-royal-100 mb-1.5">
                  Birthday *
                  <span className="text-royal-200/60 font-normal ml-1">— for birthday auto send</span>
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <select value={hmBirthmonth} onChange={(e) => setHmBirthmonth(e.target.value)}
                    className="px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white focus:outline-none focus:border-gold/50 transition-colors">
                    <option value="">Month</option>
                    {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => <option key={m} value={m}>{['','Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][m]}</option>)}
                  </select>
                  <select value={hmBirthdate} onChange={(e) => setHmBirthdate(e.target.value)}
                    className="px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white focus:outline-none focus:border-gold/50 transition-colors">
                    <option value="">Day</option>
                    {Array.from({length: 31}, (_, i) => i + 1).map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm text-royal-100 mb-1.5">Notes</label>
                <input type="text" value={hmNotes} onChange={(e) => setHmNotes(e.target.value)} placeholder="Optional"
                  className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white placeholder-royal-200 focus:outline-none focus:border-gold/50 transition-colors" />
              </div>
              <button onClick={handleAddHouseholdMember} disabled={hmSaving}
                className="w-full px-4 py-2.5 bg-gold text-royal-900 font-semibold rounded-lg text-sm hover:bg-gold-light transition-colors disabled:opacity-60">
                {hmSaving ? 'Adding...' : 'Add Household Member'}
              </button>
            </div>
          )}

          {!duplicatePrompt && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-royal-100 mb-1.5">First Name</label>
                  <input value={form.firstname} onChange={(e) => setForm({ ...form, firstname: e.target.value })}
                    className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white focus:outline-none focus:border-gold/50 transition-colors" />
                </div>
                <div>
                  <label className="block text-sm text-royal-100 mb-1.5">Last Name</label>
                  <input value={form.lastname} onChange={(e) => setForm({ ...form, lastname: e.target.value })}
                    className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white focus:outline-none focus:border-gold/50 transition-colors" />
                </div>
              </div>

              <div>
                <label className="block text-sm text-royal-100 mb-1.5">Email *</label>
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white focus:outline-none focus:border-gold/50 transition-colors" />
              </div>

              <div>
                <label className="block text-sm text-royal-100 mb-1.5">Phone</label>
                <input value={form.phone || ''} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white focus:outline-none focus:border-gold/50 transition-colors" />
              </div>

              <div>
                <label className="block text-sm text-royal-100 mb-1.5">Birthday (month & day only)</label>
                <BirthdayPicker birthmonth={form.birthmonth} birthdate={form.birthdate} onChange={({ birthmonth, birthdate }) => setForm({ ...form, birthmonth, birthdate })} />
              </div>

              <div>
                <label className="block text-sm text-royal-100 mb-1.5">Timezone</label>
                <select value={form.timezone || ''} onChange={(e) => setForm({ ...form, timezone: e.target.value || null })}
                  className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white focus:outline-none focus:border-gold/50 transition-colors">
                  <option value="">No timezone set</option>
                  {TIMEZONE_REGION_ORDER.map((region) => {
                    const tzs = timezoneGrouped[region];
                    if (!tzs?.length) return null;
                    return (
                      <optgroup key={region} label={region}>
                        {tzs.map((tz) => <option key={tz.tzcode} value={tz.tzcode}>{tz.label}</option>)}
                      </optgroup>
                    );
                  })}
                </select>
                <p className="text-royal-100 text-sm mt-1">Used for scheduled send and birthday auto-send timing. Defaults to Eastern - New York when not set.</p>
              </div>

              <div>
                <label className="block text-sm text-royal-100 mb-1.5">Notes</label>
                <textarea value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2}
                  className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white focus:outline-none focus:border-gold/50 transition-colors resize-y" />
              </div>

              {types.length > 0 && (
                <div>
                  <label className="block text-sm text-royal-100 mb-2">Groups</label>
                  <div className="flex flex-wrap gap-1.5">
                    {types.map((t) => {
                      const isSelected = form.typeIds.includes(t.contacttypeid);
                      return (
                        <button key={t.contacttypeid} onClick={() => toggleType(t.contacttypeid)}
                          className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${isSelected ? 'bg-gold/15 border-gold/40 text-gold' : 'bg-royal-800/50 border-royal-600 text-royal-100 hover:border-royal-200'}`}>
                          {t.typename}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="sticky bottom-0 flex justify-end gap-3 p-4 sm:p-6 pt-4 border-t border-royal-700/50 bg-royal-900 rounded-b-2xl">
          <button onClick={onClose} className="px-4 py-2.5 text-royal-100 text-base hover:text-white transition-colors">Cancel</button>
          {!showHouseholdAdd && (
            <Button onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : (contact ? 'Save Changes' : 'Create Contact')}</Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Contacts Page ───────────────────────────────────
export default function Contacts() {
  const { user } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [types, setTypes] = useState([]);
  const [timezoneGrouped, setTimezoneGrouped] = useState({});
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [editingContact, setEditingContact] = useState(undefined);

  const [contactLimit, setContactLimit] = useState(null);

  const [showCSVImport, setShowCSVImport] = useState(false);

  // Google import state
  const [googleImportContacts, setGoogleImportContacts] = useState(null);
  const [googleImportLoading, setGoogleImportLoading] = useState(false);
  const [googleImportError, setGoogleImportError] = useState('');

  const canUploadCSV = user?.tier?.canuploadcsv || false;
  const canImportGoogle = user?.tier?.canimportgoogle || false;

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 40 });
      if (search) params.set('search', search);
      if (filterType) params.set('type', filterType);
      const data = await api.get(`/contacts?${params.toString()}`);
      setContacts(data.contacts);
      setTotalPages(data.totalPages);
      setTotal(data.total);
    } catch (err) { console.error('Fetch contacts error:', err); }
    setLoading(false);
  }, [page, search, filterType]);

  const fetchTypes = useCallback(async () => {
    try { const data = await api.get('/contacts/types/all'); setTypes(data.types || []); }
    catch { /* ignore */ }
  }, []);

  const fetchTimezones = useCallback(async () => {
    try { const data = await api.get('/contacts/timezones', false); setTimezoneGrouped(data.grouped || {}); }
    catch { /* ignore */ }
  }, []);

  const fetchUsage = useCallback(async () => {
    try {
      const data = await api.get('/moonbeams/usage');
      setContactLimit(data.contacts);
    } catch (err) {
      console.error('Fetch usage error:', err);
    }
  }, []);

  // Handle Google OAuth callback — check URL params on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const importSource = params.get('import');
    const importCount = params.get('count');
    const importError = params.get('message');

    if (importSource === 'google' && importCount) {
      setGoogleImportLoading(true);
      setGoogleImportError('');

      contactService.getGoogleImportResults()
        .then((data) => {
          setGoogleImportContacts(data.contacts);
        })
        .catch((err) => {
          setGoogleImportError(err.message || 'Failed to retrieve imported contacts');
        })
        .finally(() => {
          setGoogleImportLoading(false);
        });

      window.history.replaceState({}, '', '/contacts');
    } else if (importSource === 'error') {
      setGoogleImportError(importError || 'Import failed. Please try again.');
      window.history.replaceState({}, '', '/contacts');
    }
  }, []);

  useEffect(() => { fetchContacts(); }, [fetchContacts]);
  useEffect(() => { fetchTypes(); }, [fetchTypes]);
  useEffect(() => { fetchTimezones(); }, [fetchTimezones]);
  useEffect(() => { fetchUsage(); }, [fetchUsage]);
  useEffect(() => { setPage(1); }, [search, filterType]);

  const handleSaveContact = () => { 
    setEditingContact(undefined); 
    fetchContacts(); 
    fetchUsage();
  };

  const handleCSVImportSuccess = () => {
    fetchContacts();
    fetchUsage();
  };

  const handleGoogleImport = () => {
    contactService.startGoogleImport();
  };

  const handleGoogleImportComplete = () => {
    setGoogleImportContacts(null);
    fetchContacts();
    fetchUsage();
  };

  // Unlimited contacts check
  const isUnlimitedContacts = contactLimit?.isUnlimited || contactLimit?.limit === -1;
  const isAtLimit = !isUnlimitedContacts && contactLimit && contactLimit.remaining === 0;

  const handleNewContact = () => {
    if (isAtLimit) return;
    setEditingContact(null);
  };

  return (
    <div className="min-h-screen bg-cream dark:bg-bg-dark transition-colors duration-300">
      <div className="max-w-4xl mx-auto px-3 sm:px-4 py-4 sm:py-6">

        {/* Dashboard Back Link */}
        <div className="mb-5">
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gold text-royal-900 font-semibold text-base hover:bg-gold-light transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Dashboard
          </Link>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
          <div>
            <h1 className="font-display text-xl sm:text-2xl text-gold-dark dark:text-gold">Contacts</h1>
            <p className="text-royal-600 dark:text-royal-100 text-sm sm:text-base">{total} contact{total !== 1 ? 's' : ''}</p>
          </div>
          <div className="flex items-center gap-2">
           {/* TODO: TEMPORARILY REMOVED — Google OAuth app is under review (submitted March 2026).
                Re-enable this button once Google approval is received.
                The canImportGoogle tier flag, OAuth routes, and import logic are all still in place.
            {canImportGoogle && (
              <button
                onClick={handleGoogleImport}
                disabled={isAtLimit}
                className="px-3 sm:px-4 py-2.5 text-royal-600 dark:text-royal-100 border border-royal-300 dark:border-royal-600 rounded-lg text-sm sm:text-base hover:text-royal-900 dark:hover:text-white hover:border-royal-500 dark:hover:border-royal-200 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
                title="Import from Google Contacts"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                <span className="hidden sm:inline">Google Contacts Import</span>
              </button>
            )}
            */}

            {canUploadCSV && (
              <button
                onClick={() => setShowCSVImport(true)}
                className="px-3 sm:px-4 py-2.5 text-royal-600 dark:text-royal-100 border border-royal-300 dark:border-royal-600 rounded-lg text-sm sm:text-base hover:text-gold hover:border-gold transition-colors"
              >
                Import CSV
              </button>
            )}
            
            {isAtLimit ? (
              <a href="/pricing" className="px-3 sm:px-4 py-2.5 bg-gold text-royal-900 font-semibold rounded-lg text-sm sm:text-base hover:bg-gold-light transition-colors">
                Upgrade to Add More
              </a>
            ) : (
              <Button onClick={handleNewContact} disabled={isAtLimit}>
                + New Contact
              </Button>
            )}
          </div>
        </div>

        {/* Google Import Error */}
        {googleImportError && (
          <div className="p-3 bg-red-900/20 border border-red-600/40 rounded-lg mb-5">
            <div className="flex items-center justify-between">
              <p className="text-red-400 text-sm">{googleImportError}</p>
              <button onClick={() => setGoogleImportError('')} className="text-red-400 hover:text-red-300 text-lg leading-none ml-3">✕</button>
            </div>
          </div>
        )}

        {/* Google Import Loading */}
        {googleImportLoading && (
          <div className="p-4 bg-royal-900 dark:bg-royal-800/40 border border-royal-700 dark:border-royal-600 rounded-lg mb-5 flex items-center gap-3">
            <div className="w-5 h-5 border-2 border-royal-600 border-t-gold rounded-full animate-spin" />
            <p className="text-royal-100 text-sm">Loading your Google contacts...</p>
          </div>
        )}

        {/* Contact Limit Banner */}
        {contactLimit && (
          <ContactLimitBanner 
            used={contactLimit.used} 
            limit={contactLimit.limit} 
            remaining={contactLimit.remaining}
            isUnlimited={isUnlimitedContacts}
          />
        )}

        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <div className="flex-1 relative">
            <input type="text" placeholder="Search contacts..." value={search} onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-3 py-2.5 bg-royal-900 dark:bg-royal-800/50 border border-royal-700 dark:border-royal-600 rounded-lg text-base text-white placeholder-royal-200 focus:outline-none focus:border-gold/50 transition-colors" />
            <svg className="absolute left-3 top-3 w-5 h-5 text-royal-100" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)}
            className="w-full sm:w-auto px-3 py-2.5 bg-royal-900 dark:bg-royal-800/50 border border-royal-700 dark:border-royal-600 rounded-lg text-base text-white focus:outline-none focus:border-gold/50 transition-colors">
            <option value="">All Groups</option>
            {types.map((t) => <option key={t.contacttypeid} value={t.contacttypeid}>{t.typename} ({t.contactcount})</option>)}
          </select>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-6 h-6 border-2 border-royal-600 border-t-gold rounded-full animate-spin" />
          </div>
        ) : contacts.length === 0 ? (
          <div className="py-10 sm:py-16">
            {search || filterType ? (
              <p className="text-royal-600 dark:text-royal-100 text-base text-center">No contacts match your filters</p>
            ) : (
              <div className="max-w-md mx-auto text-center space-y-5">
                <div className="w-16 h-16 mx-auto rounded-full bg-royal-800/60 flex items-center justify-center">
                  <svg className="w-8 h-8 text-royal-100" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>

                <div>
                  <p className="text-royal-900 dark:text-white text-lg font-medium mb-1">No contacts yet</p>
                  <p className="text-royal-600 dark:text-royal-100 text-base leading-relaxed">
                    Add your contacts here so you can send them eCards quickly. You only need to add them once — then they're always ready when you want to send.
                  </p>
                </div>

                {!isAtLimit && (
                  <div className="space-y-3">
                    <button onClick={() => setEditingContact(null)}
                      className="px-6 py-3 bg-gold text-royal-900 font-semibold rounded-lg text-base hover:bg-gold-light transition-colors shadow-md shadow-gold/20">
                      + Add Your First Contact
                    </button>

                    <div className="bg-royal-900 dark:bg-royal-800/40 border border-royal-700 dark:border-royal-700/50 rounded-xl p-4 text-left space-y-3">
                      <p className="text-gold text-sm font-medium text-center">More ways to add contacts</p>

                     {/* TODO: TEMPORARILY REMOVED — Google OAuth under review. Uncomment when approved.
                      {canImportGoogle && (
                        <button onClick={handleGoogleImport}
                          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border border-royal-600 hover:border-gold/50 hover:bg-royal-800/40 transition-all text-left">
                          <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="none">
                            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                          </svg>
                          <div>
                            <p className="text-white text-sm font-medium">Import from Google Contacts</p>
                            <p className="text-white/50 text-xs">Pull in contacts directly from your Google account</p>
                          </div>
                        </button>
                      )}
                      */}

                      {canUploadCSV && (
                        <button onClick={() => setShowCSVImport(true)}
                          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border border-royal-600 hover:border-gold/50 hover:bg-royal-800/40 transition-all text-left">
                          <svg className="w-5 h-5 text-royal-100 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <div>
                            <p className="text-white text-sm font-medium">Import via CSV Spreadsheet</p>
                            <p className="text-white/50 text-xs">Upload hundreds or thousands of contacts at once — add a "groups" column (e.g., "Family;Friends") to organize them automatically</p>
                          </div>
                        </button>
                      )}

                      {/* TODO: TEMPORARILY MODIFIED — Google OAuth under review. Restore original when approved:
                      {!canImportGoogle && !canUploadCSV && (
                        <p className="text-white/50 text-sm text-center">
                          <a href="/pricing" className="text-gold hover:text-gold-light underline">Upgrade your plan</a> to unlock bulk import via Google Contacts and CSV.
                        </p>
                      )}
                      */}
                      {!canUploadCSV && (
                        <p className="text-white/50 text-sm text-center">
                          <a href="/pricing" className="text-gold hover:text-gold-light underline">Upgrade your plan</a> to unlock bulk import via CSV.
                        </p>
                      )}
                    </div>

                    <p className="text-royal-500 dark:text-white/40 text-xs">
                      Tip: Organize contacts into groups to send eCards to many people at once — much faster than adding individually!
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {contacts.map((c) => (
              <ContactAccordion
                key={c.contactid}
                contact={c}
                onEdit={(contact) => setEditingContact(contact)}
                onDelete={() => { fetchContacts(); fetchUsage(); }}
                onRefresh={fetchContacts}
              />
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-6 px-1">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} className="text-base text-royal-600 dark:text-royal-100 hover:text-royal-900 dark:hover:text-white disabled:opacity-60 transition-colors">← Prev</button>
            <span className="text-royal-600 dark:text-royal-100 text-sm sm:text-base">Page {page} of {totalPages}</span>
            <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="text-base text-royal-600 dark:text-royal-100 hover:text-royal-900 dark:hover:text-white disabled:opacity-60 transition-colors">Next →</button>
          </div>
        )}

        {editingContact !== undefined && (
          <ContactEditModal contact={editingContact} types={types} timezoneGrouped={timezoneGrouped} onSave={handleSaveContact} onClose={() => setEditingContact(undefined)} />
        )}

        {showCSVImport && (
          <CSVImportModal
            onClose={() => setShowCSVImport(false)}
            onSuccess={handleCSVImportSuccess}
          />
        )}

        {googleImportContacts && (
          <GoogleImportModal
            contacts={googleImportContacts}
            onClose={() => setGoogleImportContacts(null)}
            onImport={handleGoogleImportComplete}
          />
        )}
      </div>
    </div>
  );
}